import { ModalButtonStyles, OpenModal, useTable } from '@composables/common'
import { SelectField } from '@src/components'
import { PlatformDocument } from '@src/types'
import { useLanguageStore } from '@src/utils/stores/languages'
import { Ref, computed, h, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export function useAssetLinkedPlatformDocuments(
    linkedIds: Ref<string[]>,
    availableDocuments: Ref<PlatformDocument[]>,
    update: (successMessage?: string | null) => Promise<void>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>
) {
    const i18n = useI18n()
    const languageStore = useLanguageStore()

    const linkedDocuments = computed(() => {
        return availableDocuments.value.filter(
            (document) => document.id && linkedIds.value.includes(document.id)
        )
    })

    const { TableWrapper: AssetLinkedPlatformDocumentsTable } = useTable({
        tableProps: {
            id: 'asset_linked_platform_documents',
            label: 'linked_platform_documents',
            help: 'help_linked_platform_documents',
            data: linkedDocuments,
            total: computed(() => linkedDocuments.value.length),
            columns: [
                {
                    label: 'name',
                    key: 'name',
                },
                {
                    label: 'language',
                    key: 'language',
                    cellRenderer: (props: { rowData: PlatformDocument }) => {
                        return (
                            languageStore.languages.find(
                                (language) =>
                                    language.value === props.rowData.language
                            )?.label || props.rowData.language
                        )
                    },
                },
            ],
            actions: [
                {
                    action: handleViewLinkedPlatformDocument,
                    icon: 'fa-eye',
                    title: 'view',
                },
            ],
        },
    })

    function handleViewLinkedPlatformDocument(document: PlatformDocument) {
        if (document.url) {
            window.open(document.url)
        }
    }

    function handleEditLinkedPlatformDocuments() {
        const selection = ref<string[]>([...linkedIds.value])
        isModalOpen.value = true

        openModal(
            {
                id: 'edit_linked_platform_documents',
                title: 'modal_title_edit_linked_platform_documents',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => saveLinkedPlatformDocuments(selection.value),
            },
            () =>
                h(SelectField, {
                    // eslint-disable-next-line quote-props
                    modelValue: selection.value,
                    'onUpdate:modelValue': (value: string[]) => {
                        selection.value = value
                    },
                    'name': 'linked_platform_documents',
                    'label': 'linked_platform_documents',
                    'help': 'help_linked_platform_documents',
                    'multiple': true,
                    'options': availableDocuments.value.map((document) => ({
                        label: `${document.name} (${document.language})`,
                        value: document.id ?? null,
                    })),
                }),
            {
                submitButtonText: 'save',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
            }
        )
    }

    function saveLinkedPlatformDocuments(selection: string[]) {
        const previous = [...linkedIds.value]
        linkedIds.value = selection

        update(i18n.t('toast_success_asset_linked_platform_documents_updated'))
            .catch((error) => {
                linkedIds.value = previous
                throw error
            })
            .then(() => {
                isModalOpen.value = false
            })
    }

    return {
        AssetLinkedPlatformDocumentsTable,
        handleEditLinkedPlatformDocuments,
    }
}
